import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Flex } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  Check,
  ChevronArrowLeft,
  KingdomIcon,
  Logo,
  RussiaIcon,
  SelectDropdown,
} from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { appSettingsActions, appSettingsSelectors } from '@shared/redux/ducks/app-settings';
import { formatNumber } from '@shared/utils/format-number';
import { AppLang } from '@shared/services/i18n/types';
import { AppTheme } from '@shared/services/theme';

import styles from './page-header.module.scss';

type PageHeaderProps = {
  title: ReactNode;
  className?: string;
  hasBalance?: boolean;
  extra?: ReactNode;
  backButtonLabel?: string;
  onBack?: () => void;
};

export const PageHeader: FC<PageHeaderProps> = ({
  title,
  className,
  hasBalance = true,
  extra,
  backButtonLabel,
  onBack,
}) => {
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'common.pageHeader' });
  const { t: tHeader } = useTranslation('translation', { keyPrefix: 'common.header' });
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const selectedLanguage = useAppSelector(appSettingsSelectors.selectLanguage);
  const selectedTheme = useAppSelector(appSettingsSelectors.selectTheme);
  const language = selectedLanguage || i18n.language;
  const theme = selectedTheme === AppTheme.dark ? AppTheme.dark : AppTheme.light;
  const dispatch = useAppDispatch();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const langSelectRef = useRef<HTMLDivElement | null>(null);
  const themeSelectRef = useRef<HTMLDivElement | null>(null);
  const balance = currentOrganization?.credit_balance;
  const formattedBalance =
    typeof balance === 'number' ? formatNumber(balance) : t('balancePlaceholder');

  const handleLanguageChange = (nextLanguage: AppLang) => {
    dispatch(appSettingsActions.setLanguage(nextLanguage));
    if (i18n.language !== nextLanguage) {
      void i18n.changeLanguage(nextLanguage);
    }
    setIsLangOpen(false);
  };

  const handleThemeChange = (nextTheme: AppTheme) => {
    dispatch(appSettingsActions.setTheme(nextTheme));
    setIsThemeOpen(false);
  };

  useEffect(() => {
    if (!isLangOpen && !isThemeOpen) return;

    const handleOutsideClick = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (langSelectRef.current && !langSelectRef.current.contains(target)) {
        setIsLangOpen(false);
      }
      if (themeSelectRef.current && !themeSelectRef.current.contains(target)) {
        setIsThemeOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLangOpen(false);
        setIsThemeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLangOpen, isThemeOpen]);

  return (
    <Flex align="center" justify="space-between" className={classNames(styles.root, className)}>
      <Flex align="center" gap={8} className={styles.titleGroup}>
        {onBack && (
          <Button
            type="text"
            icon={<ChevronArrowLeft />}
            aria-label={backButtonLabel}
            title={backButtonLabel}
            className={styles.backButton}
            onClick={onBack}
          />
        )}

        <Text variant="xl" fontWeight={500}>
          {title}
        </Text>
      </Flex>

      <Flex align="center" gap={16} className={styles.actions}>
        {extra}

        {hasBalance && (
          <Flex align="center" gap={8} className={styles.balance}>
            <Logo width={24} height={24} />

            <Text variant="md" fontWeight={500} className={styles.balanceText}>
              {t('creditsLabel', { value: formattedBalance })}
            </Text>
          </Flex>
        )}

        <div
          className={classNames(styles.select, {
            [styles.selectOpen]: isThemeOpen,
          })}
          ref={themeSelectRef}
          onMouseEnter={() => {
            setIsThemeOpen(true);
            setIsLangOpen(false);
          }}
          onMouseLeave={() => setIsThemeOpen(false)}
        >
          <button
            type="button"
            className={classNames(styles.selectTrigger, styles.themeTrigger)}
            aria-label={tHeader('theme')}
            aria-haspopup="listbox"
            aria-expanded={isThemeOpen}
            onClick={() => {
              setIsThemeOpen(true);
              setIsLangOpen(false);
            }}
          >
            <span className={styles.themeTriggerText}>
              {theme === AppTheme.dark ? t('themeDark') : t('themeLight')}
            </span>
            <span className={styles.selectChevron} aria-hidden="true">
              <SelectDropdown />
            </span>
          </button>

          <div
            className={styles.selectMenu}
            role="listbox"
            aria-label={tHeader('theme')}
            aria-hidden={!isThemeOpen}
          >
            <button
              type="button"
              role="option"
              aria-selected={theme === AppTheme.dark}
              className={styles.selectOption}
              onClick={() => handleThemeChange(AppTheme.dark)}
            >
              <span className={styles.selectOptionLabel}>{t('themeDark')}</span>
              <span className={styles.selectOptionCheck} aria-hidden="true">
                <Check />
              </span>
            </button>

            <button
              type="button"
              role="option"
              aria-selected={theme === AppTheme.light}
              className={styles.selectOption}
              onClick={() => handleThemeChange(AppTheme.light)}
            >
              <span className={styles.selectOptionLabel}>{t('themeLight')}</span>
              <span className={styles.selectOptionCheck} aria-hidden="true">
                <Check />
              </span>
            </button>
          </div>
        </div>

        <div
          className={classNames(styles.select, {
            [styles.selectOpen]: isLangOpen,
          })}
          ref={langSelectRef}
          onMouseEnter={() => {
            setIsLangOpen(true);
            setIsThemeOpen(false);
          }}
          onMouseLeave={() => setIsLangOpen(false)}
        >
          <button
            type="button"
            className={styles.selectTrigger}
            aria-label={tHeader('language')}
            aria-haspopup="listbox"
            aria-expanded={isLangOpen}
            onClick={() => {
              setIsLangOpen(true);
              setIsThemeOpen(false);
            }}
          >
            <span className={styles.selectFlag}>
              {language === AppLang.ru ? <RussiaIcon /> : <KingdomIcon />}
            </span>
            <span className={styles.selectChevron} aria-hidden="true">
              <SelectDropdown />
            </span>
          </button>

          <div
            className={styles.selectMenu}
            role="listbox"
            aria-label={tHeader('language')}
            aria-hidden={!isLangOpen}
          >
            <button
              type="button"
              role="option"
              aria-selected={language === AppLang.ru}
              aria-label="Русский"
              className={styles.selectOption}
              onClick={() => handleLanguageChange(AppLang.ru)}
            >
              <span className={styles.selectOptionLeft}>
                <RussiaIcon />
              </span>
              <span className={styles.selectOptionCheck} aria-hidden="true">
                <Check />
              </span>
            </button>

            <button
              type="button"
              role="option"
              aria-selected={language === AppLang.en}
              aria-label="English"
              className={styles.selectOption}
              onClick={() => handleLanguageChange(AppLang.en)}
            >
              <span className={styles.selectOptionLeft}>
                <KingdomIcon />
              </span>
              <span className={styles.selectOptionCheck} aria-hidden="true">
                <Check />
              </span>
            </button>
          </div>
        </div>
      </Flex>
    </Flex>
  );
};
